import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowLeft, Plus, Minus, Check, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useShop } from '../context/ShopContext';
import { useFavorites } from '../hooks/useFavorites';
import Header from '../components/Header';
import ShareButtons from '../components/ShareButtons';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { getFullImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_API_URL;

// Helper to truncate text for meta description
const truncateText = (text, maxLength = 160) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
};

export default function ProductPage() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const availableStock = product?.stock ?? 0;
    const [addedToCart, setAddedToCart] = useState(false);
    const { addToCart } = useCart();
    const { shopName, shopUrl, merchantId } = useShop();
    const { isFavorite, toggleFavorite } = useFavorites(merchantId);
    const [similarProducts, setSimilarProducts] = useState([]);
    const [similarLoading, setSimilarLoading] = useState(false);

    const fetchProduct = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/products/${id}`);
            setProduct(response.data);
        } catch (error) {
            console.error('Failed to fetch product:', error);
            toast.error('Product not found');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchProduct();
    }, [fetchProduct]);

    useEffect(() => {
        if (!merchantId || !id) {
            setSimilarProducts([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setSimilarLoading(true);
                const res = await axios.get(`${API_URL}/storefront/products/${id}/similar`, {
                    params: { owner_id: merchantId, limit: 8 },
                });
                if (!cancelled) {
                    setSimilarProducts(Array.isArray(res.data) ? res.data : []);
                }
            } catch {
                if (!cancelled) setSimilarProducts([]);
            } finally {
                if (!cancelled) setSimilarLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [merchantId, id]);

    useEffect(() => {
        if (product && product.stock !== undefined && quantity > product.stock) {
            setQuantity(Math.max(0, product.stock));
        }
    }, [product, quantity]);

    const handleAddToCart = async () => {
        if (!product) return;
        const qty = Math.min(quantity, product.stock ?? 0);
        if (qty <= 0) return;
        const success = await addToCart(product, qty);
        if (success) {
            setAddedToCart(true);
            setTimeout(() => setAddedToCart(false), 2000);
        }
    };

    // Generate SEO meta tags
    const getSeoData = () => {
        if (!product) return { title: shopName, description: '', productUrl: '' };
        const base = shopUrl || (typeof window !== 'undefined' ? window.location.origin : '');
        const productUrl = `${base.replace(/\/$/, '')}/product/${id}`;
        const price = product.price ? `€${product.price.toFixed(2)}` : '';
        const title = `${product.name}${price ? ` - ${price}` : ''} | ${shopName}`;
        const description = truncateText(product.description || `Αγοράστε ${product.name} από ${shopName}`);
        const imageUrl = getFullImageUrl(product.image_url);
        const currentUrl = typeof window !== 'undefined' ? window.location.href : productUrl;
        return { title, description, imageUrl, currentUrl, productUrl, price };
    };

    const seo = getSeoData();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-display text-2xl font-semibold text-primary mb-4">
                        Product not found
                    </h2>
                    <Link to="/">
                        <Button className="rounded-full">Back to Shop</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen min-h-[100dvh] bg-background">
            {/* Dynamic SEO Meta Tags */}
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <meta name="robots" content="index, follow" />
                {seo.productUrl && <link rel="canonical" href={seo.productUrl} />}

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="product" />
                <meta property="og:site_name" content={shopName} />
                <meta property="og:title" content={seo.title} />
                <meta property="og:description" content={seo.description} />
                {seo.imageUrl && <meta property="og:image" content={seo.imageUrl} />}
                <meta property="og:url" content={seo.currentUrl} />
                {product?.price != null && <meta property="product:price:amount" content={String(product.price)} />}
                {product?.price != null && <meta property="product:price:currency" content="EUR" />}

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seo.title} />
                <meta name="twitter:description" content={seo.description} />
                {seo.imageUrl && <meta name="twitter:image" content={seo.imageUrl} />}

                {/* Product Schema for Google */}
                {product && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Product",
                            "name": product.name,
                            "description": product.description || '',
                            "image": seo.imageUrl,
                            "url": seo.productUrl || seo.currentUrl,
                            "sku": product.sku || undefined,
                            "offers": {
                                "@type": "Offer",
                                "price": product.price,
                                "priceCurrency": "EUR",
                                "availability": (product.stock ?? 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                "url": seo.productUrl || seo.currentUrl
                            }
                        })}
                    </script>
                )}
            </Helmet>

            <Header />

            <main className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Shop
                </Link>

                <div className="grid md:grid-cols-2 gap-12">
                    {/* Product Image */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="aspect-square rounded-2xl overflow-hidden bg-gray-100"
                    >
                        <img
                            src={getFullImageUrl(product.image_url)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    </motion.div>

                    {/* Product Details */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        {product.category && (
                            <span className="text-sm text-muted-foreground uppercase tracking-wide">
                                {product.category}
                            </span>
                        )}
                        
                        <div className="flex items-start justify-between gap-3">
                            <h1 className="font-display text-4xl font-semibold text-primary flex-1">
                                {product.name}
                            </h1>
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className={`shrink-0 h-11 w-11 rounded-full ${
                                    isFavorite(product.id) ? 'border-red-200 bg-red-50 text-red-600' : ''
                                }`}
                                aria-label={isFavorite(product.id) ? 'Αφαίρεση από αγαπημένα' : 'Προσθήκη σε αγαπημένα'}
                                onClick={() => toggleFavorite(product.id)}
                                data-testid="product-favorite-btn"
                            >
                                <Heart
                                    className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`}
                                />
                            </Button>
                        </div>

                        {product.sku && (
                            <p className="text-xs text-muted-foreground font-mono">SKU: {product.sku}</p>
                        )}
                        
                        <p className="text-3xl font-semibold">€{product.price.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">
                            {availableStock > 0 ? `${availableStock} σε απόθεμα` : 'Εξαντλημένο'}
                        </p>
                        {product.description && (
                            <p className="text-muted-foreground leading-relaxed">
                                {product.description}
                            </p>
                        )}

                        {/* Quantity Selector */}
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-medium">Ποσότητα:</span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10 rounded-full"
                                    disabled={availableStock <= 0}
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                >
                                    <Minus className="w-4 h-4" />
                                </Button>
                                <span className="w-12 text-center font-medium text-lg">{Math.min(quantity, availableStock)}</span>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10 rounded-full"
                                    disabled={quantity >= availableStock}
                                    onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Add to Cart */}
                        <Button
                            onClick={handleAddToCart}
                            className={`w-full h-14 rounded-full text-lg transition-all ${
                                addedToCart ? 'bg-green-600 hover:bg-green-600' : ''
                            }`}
                            disabled={addedToCart || availableStock <= 0}
                        >
                            {addedToCart ? (
                                <>
                                    <Check className="w-5 h-5 mr-2" />
                                    Added to Cart!
                                </>
                            ) : (
                                <>
                                    <ShoppingBag className="w-5 h-5 mr-2" />
                                    Add to Cart - €{(product.price * quantity).toFixed(2)}
                                </>
                            )}
                        </Button>

                        {/* Stock Info */}
                        {product.stock !== undefined && product.stock > 0 && (
                            <p className="text-sm text-green-600">
                                In Stock ({product.stock} available)
                            </p>
                        )}

                        {/* Share Buttons */}
                        <div className="pt-4 border-t">
                            <ShareButtons 
                                url={window.location.href}
                                title={`${product.name} - ${shopName}`}
                                description={product.description}
                                type="product"
                            />
                        </div>
                    </motion.div>
                </div>

                {merchantId && (
                    <section className="mt-16 pt-12 border-t border-border" aria-label="Παρόμοια προϊόντα">
                        <h2 className="font-display text-2xl font-semibold text-primary mb-6">
                            Σου αρέσουν κι αυτά;
                        </h2>
                        <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
                            Επιλογές που ταιριάζουν στο στυλ σας, ή άλλα δημοφιλή από το ίδιο κατάστημα.
                        </p>
                        {similarLoading ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="animate-pulse">
                                        <div className="aspect-square bg-muted rounded-2xl mb-3" />
                                        <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                                        <div className="h-4 bg-muted rounded w-1/4" />
                                    </div>
                                ))}
                            </div>
                        ) : similarProducts.length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                {similarProducts.map((p, index) => (
                                    <motion.div
                                        key={p.id}
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.04 }}
                                    >
                                        <Link to={`/product/${p.id}`} className="group block">
                                            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-3">
                                                <img
                                                    src={getFullImageUrl(p.image_url)}
                                                    alt={p.name}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                            </div>
                                            <h3 className="font-medium text-primary truncate">{p.name}</h3>
                                            <p className="text-lg font-semibold">€{Number(p.price).toFixed(2)}</p>
                                        </Link>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="w-full mt-2 rounded-full"
                                            onClick={() => addToCart(p, 1)}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Στο καλάθι
                                        </Button>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-foreground">
                                <Link to="/" className="text-primary underline-offset-4 hover:underline">
                                    Μεταβείτε στον κατάλογο
                                </Link>
                                {' '}για να δείτε κι άλλα προϊόντα.
                            </p>
                        )}
                    </section>
                )}
            </main>
            <Footer shopName={shopName} />
        </div>
    );
}
